import { useState, useRef, useEffect } from 'react'
import { X, Send, Bug, Lightbulb, Loader2, Mic, CheckCircle2, ArrowRight, Bot, User, Pencil, SkipForward } from 'lucide-react'
import { clsx } from 'clsx'
import ReactMarkdown from 'react-markdown'
import { useAuthStore } from '@store/authStore'
import { useFeedbackStore } from '../store'
import { feedbackApi } from '@api/client'
import { useVoiceInput } from '@features/target-accounts/hooks/useVoiceInput'
import { showToast } from '@store/uiStore'
import type { TicketType, SubmitFeedbackResult } from '../types'

interface FeedbackModalProps {
  onClose: () => void
}

type Step = 'input' | 'questions' | 'review' | 'submitted'

interface ChatMessage {
  role: 'user' | 'assistant'
  content: string
}

export function FeedbackModal({ onClose }: FeedbackModalProps) {
  const { user } = useAuthStore()
  const { submitFeedback, isSubmitting } = useFeedbackStore()

  const [step, setStep] = useState<Step>('input')
  const [type, setType] = useState<TicketType>('bug')
  const [description, setDescription] = useState('')
  const [input, setInput] = useState('')
  const [isRefining, setIsRefining] = useState(false)
  const [result, setResult] = useState<SubmitFeedbackResult | null>(null)

  // Conversational state
  const [messages, setMessages] = useState<ChatMessage[]>([])
  const [questions, setQuestions] = useState<string[]>([])
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0)
  const [answers, setAnswers] = useState<{ question: string; answer: string }[]>([])
  const [structuredFeedback, setStructuredFeedback] = useState('')

  const inputRef = useRef<HTMLTextAreaElement>(null)
  const messagesEndRef = useRef<HTMLDivElement>(null)

  // Voice input
  const { isListening, isSupported, transcript, error: voiceError, startListening, stopListening, resetTranscript } = useVoiceInput()

  // Focus input on mount and step changes
  useEffect(() => {
    setTimeout(() => inputRef.current?.focus(), 100)
  }, [step, currentQuestionIndex])

  // Scroll to bottom on new messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages, step])

  // Sync live transcript
  useEffect(() => {
    if (isListening && transcript) {
      setInput(transcript)
    }
  }, [transcript, isListening])

  // Finalise transcript
  const prevListeningRef = useRef(false)
  useEffect(() => {
    if (prevListeningRef.current && !isListening && transcript) {
      setInput(transcript)
      resetTranscript()
      inputRef.current?.focus()
    }
    prevListeningRef.current = isListening
  }, [isListening, transcript, resetTranscript])

  // ─── Step 1: Initial description submitted ────────────────

  const handleInitialSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    const text = input.trim()
    if (!text || isRefining) return
    if (isListening) stopListening()

    setDescription(text)
    setMessages([{ role: 'user', content: text }])
    setInput('')
    setIsRefining(true)

    try {
      const response = await feedbackApi.refine({ description: text, type })

      const refineData = response.success ? response.data : null
      if (refineData) {
        if (refineData.ready && refineData.structured) {
          // AI decided no questions needed — go straight to review
          setStructuredFeedback(refineData.structured)
          setMessages(prev => [
            ...prev,
            { role: 'assistant', content: 'Thanks! I\'ve structured your feedback for you. Please review it below and edit if needed.' },
          ])
          setStep('review')
        } else if (refineData.questions && refineData.questions.length > 0) {
          setQuestions(refineData.questions)
          setCurrentQuestionIndex(0)
          setMessages(prev => [
            ...prev,
            { role: 'assistant', content: `Thanks for that! I have a few quick questions to help me understand this better:\n\n**${refineData.questions![0]}**` },
          ])
          setStep('questions')
        } else {
          // Fallback: no questions, no structured — go to review with raw description
          setStructuredFeedback(text)
          setStep('review')
        }
      } else {
        // API error — skip refinement, go straight to review
        setStructuredFeedback(text)
        setStep('review')
      }
    } catch {
      setStructuredFeedback(text)
      setStep('review')
    } finally {
      setIsRefining(false)
    }
  }

  // ─── Step 2: Answer probing questions ─────────────────────

  const handleAnswerSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    const answer = input.trim()
    if (!answer || isRefining) return
    if (isListening) stopListening()

    const currentQuestion = questions[currentQuestionIndex]
    const newAnswers = [...answers, { question: currentQuestion, answer }]
    setAnswers(newAnswers)
    setInput('')

    setMessages(prev => [
      ...prev,
      { role: 'user', content: answer },
    ])

    const nextIndex = currentQuestionIndex + 1

    if (nextIndex < questions.length) {
      // More questions to ask
      setCurrentQuestionIndex(nextIndex)
      setMessages(prev => [
        ...prev,
        { role: 'assistant', content: `**${questions[nextIndex]}**` },
      ])
    } else {
      // All questions answered — get structured rewrite
      setIsRefining(true)
      setMessages(prev => [
        ...prev,
        { role: 'assistant', content: 'Great, let me put together a structured version of your feedback...' },
      ])

      try {
        const response = await feedbackApi.refine({
          description,
          type,
          answers: newAnswers,
        })

        if (response.success && response.data?.structured) {
          setStructuredFeedback(response.data.structured)
          setMessages(prev => [
            ...prev,
            { role: 'assistant', content: 'Here\'s the structured version. Review and edit it if needed, then submit when you\'re happy with it.' },
          ])
        } else {
          setStructuredFeedback(description)
        }
      } catch {
        setStructuredFeedback(description)
      } finally {
        setIsRefining(false)
        setStep('review')
      }
    }
  }

  const handleSkipQuestions = async () => {
    if (isRefining) return
    setIsRefining(true)

    const currentAnswers = answers.length > 0 ? answers : undefined

    setMessages(prev => [
      ...prev,
      { role: 'user', content: '(Skipped remaining questions)' },
      { role: 'assistant', content: 'No problem! Let me structure what I have so far...' },
    ])

    try {
      const response = await feedbackApi.refine({
        description,
        type,
        answers: currentAnswers,
      })

      if (response.success && response.data?.structured) {
        setStructuredFeedback(response.data.structured)
        setMessages(prev => [
          ...prev,
          { role: 'assistant', content: 'Here\'s the structured version. Review and edit it if needed.' },
        ])
      } else {
        setStructuredFeedback(description)
      }
    } catch {
      setStructuredFeedback(description)
    } finally {
      setIsRefining(false)
      setStep('review')
    }
  }

  // ─── Step 3: Review & Submit ──────────────────────────────

  const handleFinalSubmit = async () => {
    if (isSubmitting || !user) return

    const submitResult = await submitFeedback({
      description: structuredFeedback.trim(),
      type,
      user_email: user.email,
      user_name: user.name,
    })

    if (submitResult) {
      setResult(submitResult)
      setStep('submitted')
      if (submitResult.action === 'new') {
        showToast(`Created ticket: ${submitResult.ticket.title}`, 'success')
      } else {
        showToast(`Added to existing ticket: ${submitResult.ticket.title}`, 'info')
      }
    }
  }

  // ─── Reset ────────────────────────────────────────────────

  const handleSubmitAnother = () => {
    setStep('input')
    setType('bug')
    setDescription('')
    setInput('')
    setMessages([])
    setQuestions([])
    setCurrentQuestionIndex(0)
    setAnswers([])
    setStructuredFeedback('')
    setResult(null)
  }

  // ─── Shared input handler ─────────────────────────────────

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      if (step === 'input') handleInitialSubmit(e)
      else if (step === 'questions') handleAnswerSubmit(e)
    }
  }

  // ─── Render ───────────────────────────────────────────────

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-[var(--bg-primary)] rounded-xl shadow-2xl w-full max-w-2xl flex flex-col max-h-[85vh]">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-[var(--border-color)]">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-amber-500 to-orange-500 flex items-center justify-center">
              {type === 'bug' ? <Bug size={20} className="text-white" /> : <Lightbulb size={20} className="text-white" />}
            </div>
            <div>
              <h2 className="text-lg font-semibold text-[var(--text-primary)]">Report Feedback</h2>
              <p className="text-sm text-[var(--text-muted)]">
                {step === 'input' && 'Tell us what\'s on your mind'}
                {step === 'questions' && 'A few quick questions...'}
                {step === 'review' && 'Review your structured feedback'}
                {step === 'submitted' && 'Feedback submitted'}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-lg text-[var(--text-muted)] hover:text-[var(--text-primary)] hover:bg-[var(--bg-tertiary)] transition-colors"
          >
            <X size={20} />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {step === 'submitted' && result ? (
            /* ── Success State ── */
            <div className="text-center space-y-4 py-4">
              <div className="w-16 h-16 rounded-full bg-green-500/10 flex items-center justify-center mx-auto">
                <CheckCircle2 size={32} className="text-green-500" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-[var(--text-primary)]">
                  {result.action === 'new' ? 'Ticket Created' : 'Added to Existing Ticket'}
                </h3>
                <p className="text-sm text-[var(--text-secondary)] mt-1">
                  {result.action === 'new'
                    ? 'Your feedback has been logged as a new ticket.'
                    : 'Your feedback was matched to a similar existing ticket and appended.'}
                </p>
              </div>
              <div className="bg-[var(--bg-secondary)] border border-[var(--border-color)] rounded-lg p-4 text-left space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-[var(--text-primary)]">{result.ticket.title}</span>
                  <span className={clsx(
                    'text-xs px-2 py-0.5 rounded-full font-medium',
                    result.ticket.priority === 'critical' && 'bg-red-500/10 text-red-500',
                    result.ticket.priority === 'high' && 'bg-orange-500/10 text-orange-500',
                    result.ticket.priority === 'medium' && 'bg-blue-500/10 text-blue-500',
                    result.ticket.priority === 'low' && 'bg-gray-500/10 text-gray-500',
                  )}>
                    {result.ticket.priority}
                  </span>
                </div>
                {result.ticket.ai_summary && (
                  <p className="text-xs text-[var(--text-muted)]">{result.ticket.ai_summary}</p>
                )}
                {result.action === 'append' && (
                  <p className="text-xs text-amber-500 flex items-center gap-1">
                    <ArrowRight size={12} />
                    Priority was bumped due to duplicate report
                  </p>
                )}
              </div>
              <div className="flex items-center justify-center gap-3 pt-2">
                <button onClick={handleSubmitAnother} className="text-sm px-4 py-2 rounded-lg bg-[var(--bg-secondary)] text-[var(--text-secondary)] hover:text-[var(--text-primary)] hover:bg-[var(--bg-tertiary)] border border-[var(--border-color)] transition-colors">
                  Submit Another
                </button>
                <button onClick={onClose} className="text-sm px-4 py-2 rounded-lg bg-primary text-white hover:bg-primary/90 transition-colors">
                  Done
                </button>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              {/* ── Type Selector (only on input step) ── */}
              {step === 'input' && messages.length === 0 && (
                <div className="space-y-2">
                  <label className="text-sm font-medium text-[var(--text-secondary)]">What are you reporting?</label>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => setType('bug')}
                      className={clsx(
                        'flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium border transition-all',
                        type === 'bug'
                          ? 'bg-red-500/10 text-red-500 border-red-500/30'
                          : 'bg-[var(--bg-secondary)] text-[var(--text-secondary)] border-[var(--border-color)] hover:bg-[var(--bg-tertiary)]'
                      )}
                    >
                      <Bug size={16} />
                      Bug Report
                    </button>
                    <button
                      type="button"
                      onClick={() => setType('feature')}
                      className={clsx(
                        'flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium border transition-all',
                        type === 'feature'
                          ? 'bg-purple-500/10 text-purple-500 border-purple-500/30'
                          : 'bg-[var(--bg-secondary)] text-[var(--text-secondary)] border-[var(--border-color)] hover:bg-[var(--bg-tertiary)]'
                      )}
                    >
                      <Lightbulb size={16} />
                      Feature Request
                    </button>
                  </div>
                </div>
              )}

              {/* ── Chat Messages ── */}
              {messages.length > 0 && (
                <div className="space-y-3">
                  {messages.map((msg, idx) => (
                    <div key={idx} className="flex gap-3">
                      <div className={clsx(
                        'w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0',
                        msg.role === 'user'
                          ? 'bg-primary/10'
                          : 'bg-gradient-to-br from-amber-500 to-orange-500'
                      )}>
                        {msg.role === 'user'
                          ? <User size={14} className="text-primary" />
                          : <Bot size={14} className="text-white" />
                        }
                      </div>
                      <div className={clsx(
                        'flex-1 rounded-lg p-3 text-sm',
                        msg.role === 'user'
                          ? 'bg-[var(--bg-secondary)]'
                          : 'bg-[var(--bg-tertiary)]'
                      )}>
                        <ReactMarkdown
                          components={{
                            p: ({ children }) => <p className="text-sm text-[var(--text-primary)] leading-relaxed">{children}</p>,
                            strong: ({ children }) => <strong className="font-semibold text-[var(--text-primary)]">{children}</strong>,
                          }}
                        >
                          {msg.content}
                        </ReactMarkdown>
                      </div>
                    </div>
                  ))}

                  {/* Loading indicator */}
                  {isRefining && (
                    <div className="flex gap-3">
                      <div className="w-7 h-7 rounded-full bg-gradient-to-br from-amber-500 to-orange-500 flex items-center justify-center flex-shrink-0">
                        <Bot size={14} className="text-white" />
                      </div>
                      <div className="flex-1 rounded-lg p-3 bg-[var(--bg-tertiary)]">
                        <div className="flex items-center gap-2 text-[var(--text-muted)]">
                          <Loader2 size={14} className="animate-spin" />
                          <span className="text-sm">Thinking...</span>
                        </div>
                      </div>
                    </div>
                  )}

                  <div ref={messagesEndRef} />
                </div>
              )}

              {/* ── Review Step: Editable structured feedback ── */}
              {step === 'review' && (
                <div className="space-y-3">
                  <div className="flex items-center gap-2">
                    <Pencil size={14} className="text-[var(--text-muted)]" />
                    <label className="text-sm font-medium text-[var(--text-secondary)]">
                      Structured feedback — edit if needed
                    </label>
                  </div>
                  <textarea
                    value={structuredFeedback}
                    onChange={(e) => setStructuredFeedback(e.target.value)}
                    rows={10}
                    className="w-full px-4 py-3 rounded-lg border border-[var(--border-color)] bg-[var(--bg-secondary)] text-[var(--text-primary)] text-sm font-mono resize-none focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary"
                  />
                  <div className="flex items-center gap-3">
                    <button
                      onClick={handleFinalSubmit}
                      disabled={!structuredFeedback.trim() || isSubmitting}
                      className={clsx(
                        'flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-lg text-sm font-medium transition-all',
                        structuredFeedback.trim() && !isSubmitting
                          ? 'bg-primary text-white hover:bg-primary/90'
                          : 'bg-[var(--bg-tertiary)] text-[var(--text-muted)] cursor-not-allowed'
                      )}
                    >
                      {isSubmitting ? (
                        <>
                          <Loader2 size={16} className="animate-spin" />
                          Submitting...
                        </>
                      ) : (
                        <>
                          <Send size={16} />
                          Submit Feedback
                        </>
                      )}
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* ── Input Section (for input + questions steps) ── */}
        {(step === 'input' || step === 'questions') && !isRefining && (
          <div className="border-t border-[var(--border-color)] p-4">
            <form onSubmit={step === 'input' ? handleInitialSubmit : handleAnswerSubmit} className="space-y-2">
              <div className="relative">
                <textarea
                  ref={inputRef}
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder={
                    step === 'input'
                      ? type === 'bug'
                        ? 'Describe the problem you encountered...'
                        : 'Describe the feature you\'d like to see...'
                      : 'Type your answer...'
                  }
                  rows={step === 'input' && messages.length === 0 ? 4 : 2}
                  className={clsx(
                    'w-full px-4 py-3 rounded-lg border bg-[var(--bg-secondary)] text-[var(--text-primary)] placeholder-[var(--text-muted)] resize-none',
                    'focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary',
                    'border-[var(--border-color)]',
                    isSupported ? 'pr-24' : 'pr-12',
                    isListening && 'ring-2 ring-red-500/50 border-red-500/50'
                  )}
                  disabled={isRefining || isSubmitting}
                />
                {isSupported && (
                  <button
                    type="button"
                    onClick={isListening ? stopListening : startListening}
                    disabled={isRefining || isSubmitting}
                    className={clsx(
                      'absolute right-14 bottom-3 p-2 rounded-lg transition-all',
                      isListening
                        ? 'bg-red-500 text-white animate-pulse'
                        : 'bg-[var(--bg-tertiary)] text-[var(--text-muted)] hover:text-[var(--text-primary)] hover:bg-[var(--bg-secondary)]'
                    )}
                    title={isListening ? 'Stop recording' : 'Voice input'}
                  >
                    <Mic size={18} />
                  </button>
                )}
                <button
                  type="submit"
                  disabled={!input.trim() || isRefining || isSubmitting}
                  className={clsx(
                    'absolute right-3 bottom-3 p-2 rounded-lg transition-colors',
                    input.trim() && !isRefining
                      ? 'bg-primary text-white hover:bg-primary/90'
                      : 'bg-[var(--bg-tertiary)] text-[var(--text-muted)] cursor-not-allowed'
                  )}
                >
                  <Send size={18} />
                </button>
              </div>

              <div className="flex items-center justify-between">
                {isListening ? (
                  <p className="text-xs text-red-500 flex items-center gap-1.5">
                    <span className="w-2 h-2 rounded-full bg-red-500 animate-pulse" />
                    Listening... speak now.
                  </p>
                ) : voiceError ? (
                  <p className="text-xs text-red-500">{voiceError}</p>
                ) : (
                  <p className="text-xs text-[var(--text-muted)]">
                    Enter to send, Shift+Enter for new line{isSupported ? ', or use the mic' : ''}
                  </p>
                )}

                {step === 'questions' && (
                  <button
                    type="button"
                    onClick={handleSkipQuestions}
                    className="flex items-center gap-1 text-xs text-[var(--text-muted)] hover:text-[var(--text-primary)] px-2 py-1 rounded hover:bg-[var(--bg-tertiary)] transition-colors"
                  >
                    <SkipForward size={12} />
                    Skip remaining
                  </button>
                )}
              </div>
            </form>
          </div>
        )}
      </div>
    </div>
  )
}
